import { lignesDe, Ordonnance } from '../../../../models/ordonnance';
import { PDF_CACHET, PDF_CACHET_RATIO } from '../examen/pdf-cachet';
import { PDF_LOGO_ENTITE } from './pdf-entite';

// Phrase imprimée sur toutes les ordonnances, non modifiable
export const TEMPS_ATTENTE = "TEMPS D'ATTENTE ≈ 1 MOIS";

// Lieu, GSM et bandeau arabe du cabinet : toujours les mêmes, jamais saisis (papier à en-tête photographié le
// 2026-09-23, « Monastir » déjà confirmé par le client comme valeur figée).
const LIEU = 'Monastir';
const GSM = '97 77 51 05 - 58 775 105';
// pdfMake NE réordonne PAS les mots d'un texte arabe (vérifié par extraction des coordonnées exactes des
// glyphes via `pdftotext -bbox` le 2026-09-23 : chaque mot est posé de gauche à droite dans l'ordre où il est
// tapé, sans appliquer l'algorithme bidi — seul l'assemblage des LETTRES à l'intérieur d'un même mot est
// correct). Pour qu'un mot apparaisse à DROITE, il doit donc être tapé EN DERNIER. ARABE_TITRE/ARABE_NOM sont
// donc écrits dans l'ordre visuel inverse de la lecture (dernier mot tapé = mot le plus à droite à l'écran).
// ⚠️ Un seul espace entre 2 mots arabes ne produit AUCUN espacement visible dans Aref Ruqaa ni Noto Naskh
// Arabic (vérifié par rendu le 2026-09-23 : « طبيب طبيب » avec 1 espace s'affichait collé « طبيبيطري ») — le
// glyphe espace de ces polices a une largeur quasi nulle. Un DOUBLE espace produit un espacement net et
// régulier, sans jamais casser la liaison des lettres à l'intérieur d'un même mot (testé, confirmé). D'où les
// doubles espaces ci-dessous entre chaque mot arabe.
const ARABE_TITRE = 'بيطري  طبيب'; // affiché : طبيب (à droite) بيطري (à gauche), lu droite→gauche = « طبيب بيطري »
// Nom arabe. La lettre maghrébine « ڨ » (qaf à 3 points, son « G » de « Gall ») donnée par le client n'est
// couverte ni par Noto Naskh Arabic ni par Aref Ruqaa (testé le 2026-09-23, carré vide dans les deux cas) :
// qaf standard (ق) utilisé à la place, pour rester visuellement cohérent avec le reste du mot dans une seule
// police plutôt que de casser la liaison des lettres avec une police différente pour cette seule lettre.
const ARABE_NOM = 'قال  مجدي  الدكتور'; // lu droite→gauche (affichage correct) : « الدكتور مجدي قال »
// Adresse arabe du pied de page, dans l'ordre visuel correct (le tableau est lu de droite à gauche : dernier
// élément du tableau = le plus à droite à l'écran). Segments latins (chiffres, tiret) en police Roboto — la
// police arabe n'a pas de glyphes latins. Segments arabes en Noto Naskh Arabic à 14 pt (le client a demandé
// « Traditional Arabic », police Microsoft propriétaire indisponible ici — voir pdf-setup.ts), avec un
// espacement de caractères (`characterSpacing`) de 1,75 pt en plus du double espace ci-dessus, pour l'aspect
// « aéré » du papier réel — vérifié par rendu que characterSpacing seul ne suffit pas à séparer les mots
// (voir note plus haut), mais s'ajoute correctement par-dessus le double espace sans casser les liaisons.
// « R9 » en Liberation Serif gras 12 pt (le client a demandé Times New Roman, propriétaire — Liberation Serif
// est son équivalent libre compatible au point près).
const ARABE_ADRESSE: any[] = [
	{ text: '5000 ', font: 'Roboto' },
	{ text: 'المنستير  ', font: 'NotoNaskhArabic', fontSize: 14, characterSpacing: 1.75 },
	{ text: '- ', font: 'Roboto' },
	{ text: 'R9 ', font: 'LiberationSerifBold', fontSize: 12, bold: true },
	{ text: 'حي  النسيم', font: 'NotoNaskhArabic', fontSize: 14, characterSpacing: 1.75 },
];

const TEAL = '#0f6b63';
const INK = '#1f2d3d';
const GREY = '#6b7280';
// Bleu marine du papier à en-tête réel (nom du vétérinaire, logo, ligne GSM) — mode « avec entité » seulement.
const MARINE = '#1b3a6b';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août',
	'septembre', 'octobre', 'novembre', 'décembre'];

/** « 2026-09-12 » → « 12 septembre 2026 » (sans passer par Date : évite les décalages de fuseau). */
export function formatDateFr(iso: string): string {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
	return m ? `${parseInt(m[3], 10)} ${MOIS[parseInt(m[2], 10) - 1]} ${m[1]}` : String(iso || '');
}

function label(text: string, alignment = 'left'): any {
	return { text, fontSize: 7, bold: true, color: TEAL, characterSpacing: 0.6, alignment };
}

// A5 PORTRAIT (correction du 2026-09-23 : la photo du papier à en-tête, remise à l'endroit, montre une feuille
// plus haute que large — le « paysage » évoqué juste avant venait d'une photo prise de travers). 419,53 ×
// 595,28 pt (14,8 × 21 cm), conforme au format standard d'une ordonnance vétérinaire tunisienne.
//   gauche/droite : 0,15 cm ≈ 4 pt chacune — largeur de contenu 14,5 cm sur 14,8 cm de feuille, mesurée par
//     le client sur le papier physique.
const MARGE_COTE = 4;
const LARGEUR_PAGE_A5 = 419.53;
const LARGEUR_CONTENU = LARGEUR_PAGE_A5 - 2 * MARGE_COTE; // ≈ 411,5

// Sans entité : la feuille EST le papier à en-tête du cabinet. La bande haute (logo, nom, GSM, « Monastir, le »)
// et la bande basse (adresse) y sont DÉJÀ imprimées ; ces marges leur laissent la place, mesurée par le client
// (4,5 cm / 1,5 cm).
const MARGE_HAUT_SANS_ENTITE = 128;
const MARGE_BAS_SANS_ENTITE = 43;

// Avec entité : rien n'est pré-imprimé, mais le bandeau qu'on imprime nous-mêmes (nom + logo + titre + GSM +
// date/lieu) est plus compact que la bande réservée sur le papier physique — marges dimensionnées pour ce
// contenu, pas recopiées de la bande « sans entité ». pdfMake ne redimensionne PAS la zone d'en-tête au
// contenu réel : tout ce qui dépasse la hauteur réservée ici est silencieusement rogné (constaté le
// 2026-09-23 : la ligne « Monastir, le {date} » disparaissait complètement, sa position tombant juste après
// cette limite) — vérifié par extraction des coordonnées du PDF (`pdftotext -bbox`), pas seulement à l'œil.
const MARGE_HAUT_ENTITE = 92;
const MARGE_BAS_ENTITE = 34;

function rule(largeur = LARGEUR_CONTENU): any {
	return { canvas: [{ type: 'line', x1: 0, y1: 0, x2: largeur, y2: 0, lineWidth: 0.6, lineColor: '#b9b4a6' }], margin: [0, 3, 0, 3] };
}

/** Ligne « GSM : ... » encadrée de 2 filets qui n'entourent QUE le texte, pas toute la largeur de la page —
 * table à une colonne de largeur automatique, centrée par 2 colonnes flanquantes, dont seules les bordures
 * horizontales (haut/bas) sont dessinées. */
function ligneEncadree(texte: string): any {
	return {
		columns: [
			{ width: '*', text: '' },
			{
				width: 'auto',
				table: { widths: ['auto'], body: [[{ text: texte, bold: true, fontSize: 8, color: MARINE, margin: [0, 2, 0, 2] }]] },
				layout: {
					hLineWidth: () => 0.7,
					vLineWidth: () => 0,
					hLineColor: () => MARINE,
					paddingLeft: () => 6,
					paddingRight: () => 6,
					paddingTop: () => 0,
					paddingBottom: () => 0,
				},
			},
			{ width: '*', text: '' },
		],
	};
}

/** Petit losange plein (canvas `polyline` fermé) — brique de base de `ornement()`. */
function losange(cx: number, cy: number, r: number): any {
	return {
		type: 'polyline',
		closePath: true,
		color: MARINE,
		points: [{ x: cx, y: cy - r }, { x: cx + r, y: cy }, { x: cx, y: cy + r }, { x: cx - r, y: cy }],
	};
}

/** Petit ornement « filet - losange - losange - losange - filet », dessiné (pas du texte) : reproduit le motif
 * du papier réel sous chaque titre, plus fidèle que des puces de texte « • • • ». */
function ornement(): any {
	const y = 3;
	return {
		canvas: [
			{ type: 'line', x1: 0, y1: y, x2: 13, y2: y, lineWidth: 0.6, lineColor: MARINE },
			losange(18, y, 2),
			losange(26, y, 2.6),
			losange(34, y, 2),
			{ type: 'line', x1: 39, y1: y, x2: 52, y2: y, lineWidth: 0.6, lineColor: MARINE },
		],
		margin: [0, 3, 0, 0],
	};
}

/**
 * En-tête « avec entité », calqué sur la charte du papier à en-tête réel : nom + titre en français (gauche) et
 * en arabe (droite) de part et d'autre du logo, chacun avec son petit ornement dessiné, ligne GSM encadrée de
 * filets (pas sur toute la largeur — uniquement autour du texte), puis « Monastir, le {date} » — la date est
 * soulignée pour reprendre le tiret du « Monastir, le .......... » imprimé sur le papier physique.
 * Nom/titre français en Noto Serif gras italique (plus proche du style calligraphié du papier réel que Roboto,
 * un simple sans-serif penché) ; nom/titre arabe en Aref Ruqaa, style manuscrit choisi par le client (voir
 * ARABE_NOM/ARABE_TITRE plus haut).
 */
function enteteEntite(o: Ordonnance, dateOrdonnance: string, largeur = LARGEUR_CONTENU): any {
	return {
		margin: [MARGE_COTE, 10, MARGE_COTE, 0],
		stack: [
			{
				columns: [
					{
						width: '*',
						stack: [
							{ text: o.veterinaireNom || 'Docteur Gall Mejdi', font: 'NotoSerifEntite', fontSize: 13, color: MARINE },
							{ text: 'Médecin Vétérinaire', font: 'NotoSerifEntite', fontSize: 10, color: MARINE },
							ornement(),
						],
					},
					{ image: PDF_LOGO_ENTITE, width: 46, alignment: 'center', margin: [0, 2, 0, 0] },
					{
						width: '*',
						stack: [
							{ text: ARABE_NOM, font: 'ArefRuqaa', bold: true, fontSize: 13, color: MARINE, alignment: 'right' },
							{ text: ARABE_TITRE, font: 'ArefRuqaa', fontSize: 10, color: MARINE, alignment: 'right' },
							{ ...ornement(), alignment: 'right' },
						],
					},
				],
			},
			ligneEncadree(`GSM : ${GSM}`),
			{
				text: [
					{ text: `${LIEU}, le `, fontSize: 9, color: INK },
					{ text: dateOrdonnance, fontSize: 9, bold: true, color: INK, decoration: 'underline' },
				],
				alignment: 'right',
				margin: [0, 4, 0, 0],
			},
		],
	};
}

/** Pied de page « avec entité » : adresse du cabinet en arabe, comme sur le papier réel, + pagination si
 * plusieurs pages. */
function piedDePageEntite(page: number, count: number): any {
	return {
		margin: [MARGE_COTE, 0, MARGE_COTE, 8],
		stack: [
			{ text: ARABE_ADRESSE, alignment: 'center', fontSize: 8, color: GREY },
			count > 1 ? { text: `Page ${page} / ${count}`, alignment: 'center', fontSize: 7, color: GREY, margin: [0, 1, 0, 0] } : null,
		].filter(Boolean),
	};
}

/** Rappel discret (ferme/date/cage) sur les pages suivantes, en mode « sans entité » (papier à en-tête). */
function rappelSansEntite(o: Ordonnance): string {
	return `${o.ferme} — ${formatDateFr(o.dateOrdonnance)} — cage ${o.numCage}`;
}

/**
 * Contenu d'UNE ordonnance (hors en-tête/pied de page de page, gérés séparément) : ferme/cage, tableau des
 * médicaments, bloc de fin (temps d'attente, température, signature). Factorisé pour être utilisé aussi bien
 * dans le PDF A5 seul que dans les 2 colonnes du PDF A4 « 2 ordonnances par page ».
 */
function blocOrdonnance(o: Ordonnance, avecCachet: boolean, largeur = LARGEUR_CONTENU): any[] {
	const temperature = String(o.temperatureEau || '').trim();
	const lignes = lignesDe(o);
	const recommandations = String(o.recommandations || '').trim();
	const premiereLigneFin = lignes.length; // index de la dernière ligne médicament dans body (l'en-tête est à 0)

	// Un seul tableau (et non un cadre contenant un tableau) : pdfmake ne coupe qu'entre les lignes,
	// et l'en-tête des colonnes est répété si la prescription passe sur la page suivante.
	const body: any[] = [
		[label('MÉDICAMENT'), label('DOSE / POSOLOGIE PAR JOUR'), label('DURÉE')],
		...lignes.map(l => {
			const duree = String(l.duree || '').trim();
			return [
				{ text: l.medicament, bold: true, fontSize: 12, color: INK, margin: [0, 4, 0, 4] },
				{ text: l.dose, bold: true, fontSize: 11, color: TEAL, margin: [0, 4, 0, 4] },
				{ text: /^pdt\b/i.test(duree) ? duree : `pdt ${duree}`, italics: true, fontSize: 11, color: INK, margin: [0, 4, 0, 4] },
			];
		}),
		[{
			colSpan: 3,
			text: [
				{ text: "Voie d'administration : ", italics: true, color: GREY, fontSize: 9 },
				{ text: o.voieAdministration, italics: true, bold: true, color: TEAL, fontSize: 9 },
			],
			margin: [0, 3, 0, 3],
		}, {}, {}],
	];
	if (recommandations) {
		body.push([{
			colSpan: 3,
			stack: [label('RECOMMANDATIONS'), { text: recommandations, fontSize: 10, margin: [0, 3, 0, 0] }],
			margin: [0, 3, 0, 3],
		}, {}, {}]);
	}

	const signature: any[] = [{ text: 'Signature du Vétérinaire', alignment: 'right', fontSize: 10, margin: [0, 10, 0, 0] }];
	if (avecCachet) {
		const width = 170;
		signature.push({ image: PDF_CACHET, width, height: width / PDF_CACHET_RATIO, alignment: 'right', margin: [0, 5, 0, 0] });
	} else {
		signature.push({ canvas: [{ type: 'line', x1: largeur - 150, y1: 45, x2: largeur, y2: 45, lineWidth: 0.8, lineColor: INK }] });
	}

	return [
		{ stack: [label('FERME AQUACOLE'), { text: o.ferme, bold: true, fontSize: 16 }] },
		{ stack: [label('NUMÉRO DE CAGE'), { text: o.numCage, bold: true, fontSize: 14 }], margin: [0, 6, 0, 10] },
		{
			table: { headerRows: 1, widths: ['*', 112, 78], body },
			layout: {
				// Cadre extérieur, trait épais sous les titres, traits fins entre médicaments et avant la voie
				hLineWidth: (i: number, node: any) =>
					i === 0 || i === node.table.body.length ? 1
						: i === 1 ? 1.4
							: i === premiereLigneFin + 1 ? 0.6
								: i <= premiereLigneFin ? 0.4 : 0.3,
				vLineWidth: (i: number, node: any) => (i === 0 || i === node.table.widths.length ? 1 : 0),
				hLineColor: (i: number, node: any) =>
					i === 0 || i === node.table.body.length || i === 1 ? INK : '#c9c4b5',
				vLineColor: () => INK,
				paddingLeft: () => 6,
				paddingRight: () => 6,
				paddingTop: () => 2,
				paddingBottom: () => 2,
			},
		},
		// Bloc de fin gardé d'un seul tenant : jamais coupé entre deux pages
		{
			unbreakable: true,
			stack: [
				{ text: '', margin: [0, 4, 0, 0] },
				rule(largeur),
				{ text: TEMPS_ATTENTE, bold: true, fontSize: 9, alignment: 'center', characterSpacing: 0.5 },
				rule(largeur),
				{
					text: `Température de l'eau : ${temperature}${temperature && temperature.indexOf('°') === -1 ? ' °C' : ''}`,
					bold: true, fontSize: 9, alignment: 'center',
				},
				rule(largeur),
				...signature,
			],
		},
	];
}

/**
 * Ordonnance A5 portrait, sur le papier à en-tête du cabinet (avecEntite = false, mode par défaut : les zones
 * hautes/basses restent vides, déjà occupées par l'impression physique du papier) ou en version autonome
 * (avecEntite = true : en-tête, pied de page, date et lieu ajoutés au PDF pour un envoi/impression sans ce
 * papier). Ne concerne QUE l'ordonnance : le PDF d'examen (examen.component.ts) reste en A4, inchangé.
 */
export function buildOrdonnanceDocDef(o: Ordonnance, avecCachet: boolean, avecEntite: boolean): any {
	const dateOrdonnance = formatDateFr(o.dateOrdonnance);
	const resume = rappelSansEntite(o);

	return {
		pageSize: 'A5',
		pageMargins: avecEntite
			? [MARGE_COTE, MARGE_HAUT_ENTITE, MARGE_COTE, MARGE_BAS_ENTITE]
			: [MARGE_COTE, MARGE_HAUT_SANS_ENTITE, MARGE_COTE, MARGE_BAS_SANS_ENTITE],
		defaultStyle: { fontSize: 10, color: INK },
		header: (page: number) => {
			if (avecEntite) {
				return page === 1
					? enteteEntite(o, dateOrdonnance)
					: { stack: [enteteEntite(o, dateOrdonnance), { text: resume, fontSize: 7, color: GREY, margin: [MARGE_COTE, 1, MARGE_COTE, 0] }] };
			}
			// Sans entité : la zone haute reste vide (papier à en-tête), sauf un rappel discret à partir de la page 2
			return page > 1 ? { text: resume, fontSize: 8, color: GREY, margin: [MARGE_COTE, 100, MARGE_COTE, 0] } : null;
		},
		footer: (page: number, count: number) => avecEntite
			? piedDePageEntite(page, count)
			: (count > 1 ? { text: `Page ${page} / ${count}`, alignment: 'center', fontSize: 8, color: GREY, margin: [0, 0, 0, 17] } : null),
		content: blocOrdonnance(o, avecCachet),
	};
}

/**
 * 2 ordonnances DIFFÉRENTES sur une même page A4 paysage — économie de papier (une ordonnance tunisienne
 * standard est en A5 ; 2 A5 portrait côte à côte = 1 A4 paysage exactement). Toujours « avec entité » : sans
 * papier à en-tête physique pour les 2 emplacements à la fois, chaque moitié doit être autonome. Chaque
 * colonne reprend donc sa propre en-tête/pied de page (pas de header()/footer() de page, qui ne s'appliquerait
 * qu'une fois pour toute la page) : après découpe, chaque moitié est une ordonnance complète et autonome.
 */
export function buildDoubleOrdonnanceDocDef(o1: Ordonnance, avecCachet1: boolean, o2: Ordonnance, avecCachet2: boolean): any {
	const colonne = (o: Ordonnance, avecCachet: boolean): any => ({
		width: LARGEUR_PAGE_A5,
		stack: [
			enteteEntite(o, formatDateFr(o.dateOrdonnance), LARGEUR_CONTENU),
			{ margin: [MARGE_COTE, 6, MARGE_COTE, 0], stack: blocOrdonnance(o, avecCachet) },
			{ margin: [MARGE_COTE, 6, MARGE_COTE, 0], stack: [piedDePageEntite(1, 1)] },
		],
	});

	return {
		pageSize: 'A4',
		pageOrientation: 'landscape',
		pageMargins: [0, 12, 0, 12],
		defaultStyle: { fontSize: 10, color: INK },
		content: [
			{
				columns: [
					colonne(o1, avecCachet1),
					{ width: 2, canvas: [{ type: 'line', x1: 1, y1: 0, x2: 1, y2: 570, lineWidth: 0.5, lineColor: '#c9c4b5', dash: { length: 4 } }] },
					colonne(o2, avecCachet2),
				],
				columnGap: 0,
			},
		],
	};
}
